import { useState } from "react";
import { formatDate } from "../lib/formatDate";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "./ui/pagination";
import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Table,
} from "./ui/table";
import {
  useGetSwapsOfOrganizationQuery,
  useGetSwapsQuery,
} from "../generated/graphql";
import { useNavigate } from "react-router-dom";
import { formatNumberToString } from "../lib/formatNumbers";

interface SwapProps {
  swaps?: any[];
  page: number;
  onPrevious: () => void;
  onNext: () => void;
}
const SwapsTableBase = ({ swaps, page, onPrevious, onNext }: SwapProps) => {
  const navigate = useNavigate();
  const [selectedSwaps, setSelectedSwaps] = useState<string[]>([]);

  const handleExport = () => {
    if (!swaps || selectedSwaps.length === 0) return;

    // Create CSV headers
    const headers = [
      "ID",
      "Organization",
      "From Token",
      "From Amount",
      "To Token",
      "To Amount",
      "Date",
      "Deposit Address",
    ];

    // Format swap data
    const csvData = selectedSwaps
      .map((id) => swaps.find((swap) => swap.id === id))
      .filter(Boolean)
      .map((swap) => [
        swap.id,
        swap.organizationName,
        swap.fromMint?.name,
        swap.fromAmount,
        swap.toMint?.name,
        swap.toAmount ?? "N/A",
        formatDate(swap.date ?? ""),
        swap.depositAddress,
      ]);

    // Combine headers and data
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `swaps_export_${formatDate(new Date().toISOString())}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSelectAll = () => {
    if (swaps && selectedSwaps.length === 0) {
      setSelectedSwaps(swaps.map((swap) => swap.id));
    } else {
      setSelectedSwaps([]);
    }
  };

  return (
    <Card className="xl:col-span-2">
      <CardHeader className="flex flex-row items-center">
        <div className="grid gap-2">
          <CardTitle>Swaps</CardTitle>
        </div>
        <Button
          onClick={handleExport}
          style={{ marginLeft: "20px", marginTop: 0 }}
        >
          Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead align="left" style={{ paddingLeft: "0px" }}>
                <Button size="icon" onClick={handleSelectAll}>
                  All
                </Button>
              </TableHead>
              <TableHead>id</TableHead>
              <TableHead>Org</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>DepositAddress</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {swaps?.map((swap, idx) => (
              <TableRow
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/swap/${swap.id}`);
                }}
              >
                <TableCell
                  className="p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSwaps(
                      selectedSwaps.filter((id) => id !== swap.id)
                    );
                  }}
                >
                  <input
                    type="checkbox"
                    id="vehicle1"
                    name="vehicle1"
                    value="Selected"
                    checked={selectedSwaps.includes(swap.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedSwaps([...selectedSwaps, swap.id]);
                      }
                    }}
                    style={{ height: "25px", width: "25px" }}
                  />
                </TableCell>
                <TableCell>{swap.id?.slice(0, 6)}...</TableCell>
                <TableCell>{swap?.organizationName}</TableCell>
                <TableCell>{swap?.fromMint?.name}</TableCell>
                <TableCell>{formatNumberToString(swap?.fromAmount)}</TableCell>
                <TableCell>{swap?.toMint?.name}</TableCell>
                <TableCell>
                  {formatNumberToString(swap.toAmount) ?? "N/A"}
                </TableCell>
                <TableCell>{formatDate(swap.date ?? "")}</TableCell>
                <TableCell>{swap?.depositAddress}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination>
          <PaginationContent>
            {page > 1 && (
              <PaginationItem>
                <PaginationPrevious onClick={onPrevious} />
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink>{page}</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={onNext} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </CardContent>
    </Card>
  );
};

interface SwapsTableProps {
  search?: string;
}
export const SwapsTableAll = ({ search }: SwapsTableProps) => {
  const [swapPage, setSwapPage] = useState(1);
  const [{ data: swapsData }] = useGetSwapsQuery({
    variables: { page: swapPage, search: search ?? undefined },
    requestPolicy: "network-only",
  });
  const swaps = swapsData?.getSwaps;
  return (
    <SwapsTableBase
      swaps={swaps}
      page={swapPage}
      onPrevious={() => setSwapPage(swapPage - 1)}
      onNext={() => setSwapPage(swapPage + 1)}
    />
  );
};

export const SwapsOfOrganization = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const [swapPage, setSwapPage] = useState(1);
  const [{ data: swapsData }] = useGetSwapsOfOrganizationQuery({
    variables: { page: swapPage, organizationId },
    requestPolicy: "network-only",
  });
  const swaps = swapsData?.getSwapsOfOrganization;

  return (
    <SwapsTableBase
      swaps={swaps}
      page={swapPage}
      onPrevious={() => setSwapPage(swapPage - 1)}
      onNext={() => setSwapPage(swapPage + 1)}
    />
  );
};
